/**
 * Transport (W1-14, A§8, R§6.1, D§5.9): play modes (flow = Space, holdReturn = hold T, holdStay = hold U), stop with
 * optional return to the start point, seek, rate, loop, the chart-time mapping and the play-run timeline.
 *
 * Positions: while paused the transport stores a CHART time (an offset change keeps the playhead on the same beat);
 * while playing, the timeline maps context time to MUSIC time (an offset change keeps the music continuous and
 * moves the chart). `chartTime = max(0, musicPos - shift)` with `shift = offsetMs/1000 + infoOffsetSec +
 * previewAudioDelayMs/1000` (Phira clamp, R§6.1). A seek to a negative chart time reaches the music before beat 0
 * (down to music time min(0, shift)); chart readings stay clamped at 0. With music loaded, positions are capped at
 * the song end; playback stops there (play() at the end restarts from chart 0, like a media player).
 *
 * Runs: play/seek start a segment `LEAD` (30 ms) ahead and begin a new play run; a rate change splices a segment in
 * at a future context time with a continuous position (same run). The loop region [start, end) in chart time is
 * honoured when playback crosses its end (starting beyond the end plays on); a loop reaching past the song end
 * wraps at the song end, and play() at the song end starts such a loop again. Wraps are scheduled
 * sample-accurately ahead of time and start the next lap of the run. `chartTime()` is the visual
 * (latency-compensated, smoothed) clock, `audioChartTime()` the scheduling clock.
 *
 * stop(): returns to the start point when `returnToStart` is true, or (left undefined) in holdReturn mode. When the
 * song ends during a holdReturn play, the release (an idle stop()) still returns.
 */

import type { Sec } from '../core/time/types';
import type { Unsubscribe } from '../core/util/types';
import type { PlayMode, Transport, TransportState } from './types';
import type { ClockSample, MusicOutput } from './music';
import { isProvisional, segmentAt, segmentCtxAt, segmentPosAt, timelinePosAt, type Segment } from './timeline';
import { SCHEDULE_HORIZON_SEC } from './scheduler';

/** Play runs start this far ahead of the context clock (A§8: "started ~30 ms ahead"). */
export const START_LEAD_SEC = 0.03;
export const MIN_RATE = 0.0625;
export const MAX_RATE = 16;
/** Loop regions shorter than this are ignored. */
export const MIN_LOOP_SEC = 0.05;
/**
 * Element path: re-anchor the timeline on the latest sample when the smoothed measured drift exceeds this (inside the
 * "sync +-10 ms" the transport bar shows; smaller errors are left to the settle correction below).
 */
export const REANCHOR_SEC = 0.01;
/** Element path: samples averaged after an anchor or an element restart before the residual offset is corrected. */
export const SETTLE_SAMPLES = 8;
/** Element path: a settled (averaged) residual offset larger than this shifts the anchor by that average. */
export const SETTLE_TOLERANCE_SEC = 0.002;
/** Loop wraps and the song end are planned this far ahead. */
const PLAN_AHEAD_SEC = SCHEDULE_HORIZON_SEC + START_LEAD_SEC + 0.05;

export interface TransportDeps {
  /** Creates/resumes the AudioContext (rejects when Web Audio is unavailable). */
  ensureRunning(): Promise<void>;
  /** The context's currentTime, or null without a context. */
  ctxNow(): number | null;
  /** Audible, smoothed, calibrated context time for the current animation frame, or null without a context. */
  visualNow(): number | null;
  /** Audible, calibrated context time right now (not frame-aligned; for the song-end check), or null. */
  audibleNow(): number | null;
  /** The music output for a run at `rate` (buffer, element or silent path). */
  outputFor(rate: number): MusicOutput;
  /** The timeline or the play state changed: reschedule now. */
  changed(): void;
  /** Playback started or stopped (tick sources). */
  playingChanged(playing: boolean): void;
}

type Loop = { readonly start: Sec; readonly end: Sec } | null;

export class TransportImpl implements Transport {
  private readonly deps: TransportDeps;
  private st: TransportState = { playing: false, mode: 'flow', rate: 1, startChartTime: 0, loop: null };
  private readonly listeners = new Set<(s: TransportState) => void>();
  private shiftSec = 0;
  private durationSec: number | null = null;
  /** Unclamped chart time while paused. */
  private pausedChart = 0;
  /** Unclamped chart time where the current/last play started (return target). */
  private startChart = 0;
  private segs: Segment[] = [];
  private out: MusicOutput | null = null;
  private nextId = 1;
  /** Current play run (see timeline.ts: occurrences). */
  private run = 0;
  private endAt: number | null = null;
  private token = 0;
  private endedNaturally = false;
  /** Element path: exponentially smoothed error of the clock samples against the timeline. */
  private drift = 0;
  /** Element path: samples still to average in the current settle window (0 = not settling). */
  private settleLeft = 0;
  private settleSum = 0;

  constructor(deps: TransportDeps) {
    this.deps = deps;
  }

  // ------------------------------------------------------------------ readings

  get state(): TransportState {
    return this.st;
  }

  /** Timeline of the current run (empty while paused). */
  get segments(): readonly Segment[] {
    return this.segs;
  }

  /** Music seconds minus chart seconds. */
  get shift(): number {
    return this.shiftSec;
  }

  /** The output of the current run (null while paused). */
  get output(): MusicOutput | null {
    return this.out;
  }

  chartTime(): Sec {
    return Math.max(0, this.posNow('visual') - this.shiftSec);
  }

  audioChartTime(): Sec {
    return Math.max(0, this.posNow('audio') - this.shiftSec);
  }

  musicTime(): Sec {
    return this.posNow('visual');
  }

  onChange(cb: (s: TransportState) => void): Unsubscribe {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  // ------------------------------------------------------------------ commands

  async play(opts: { readonly from?: Sec; readonly mode?: PlayMode } = {}): Promise<void> {
    const token = ++this.token;
    if (opts.from !== undefined) this.seek(opts.from);
    if (this.st.playing) {
      if ((opts.mode !== undefined && opts.mode !== this.st.mode) || opts.from !== undefined) {
        this.startChart = opts.from !== undefined ? this.pausedOrPlayingChart() : this.posNow('visual') - this.shiftSec;
        this.update({ mode: opts.mode ?? this.st.mode, startChartTime: Math.max(0, this.startChart) });
      }
      return;
    }
    await this.deps.ensureRunning();
    // Another play() or a stop() may have run while the context resumed.
    if (token !== this.token || this.isPlaying()) return;
    this.startRun(opts.mode ?? 'flow');
  }

  stop(opts: { readonly returnToStart?: boolean } = {}): void {
    this.token++;
    if (this.st.playing) {
      const ret = opts.returnToStart ?? this.st.mode === 'holdReturn';
      const pos = this.posNow('visual');
      this.haltRun();
      this.pausedChart = ret ? this.startChart : this.clampPos(pos) - this.shiftSec;
      this.endedNaturally = false;
      this.st = { ...this.st, playing: false };
      this.deps.playingChanged(false);
      this.deps.changed();
      this.emit();
      return;
    }
    const ret = opts.returnToStart ?? (this.st.mode === 'holdReturn' && this.endedNaturally);
    if (ret) this.pausedChart = this.clampPos(this.startChart + this.shiftSec) - this.shiftSec;
    this.endedNaturally = false;
    this.emit();
  }

  seek(t: Sec): void {
    if (Number.isNaN(t)) return;
    const pos = this.clampPos(t + this.shiftSec);
    this.endedNaturally = false;
    if (this.st.playing) {
      const now = this.deps.ctxNow();
      if (now !== null) this.restartRun(now, now + START_LEAD_SEC, pos, false);
    } else {
      this.pausedChart = pos - this.shiftSec;
    }
    this.emit();
  }

  setRate(r: number): void {
    if (!Number.isFinite(r) || r <= 0) return;
    const rate = Math.min(MAX_RATE, Math.max(MIN_RATE, r));
    if (rate === this.st.rate) return;
    this.st = { ...this.st, rate };
    const now = this.deps.ctxNow();
    if (this.st.playing && now !== null) {
      const T = now + START_LEAD_SEC;
      const next = this.deps.outputFor(rate);
      if (next !== this.out) {
        this.restartRun(now, T, this.clampPos(timelinePosAt(this.segs, T, this.pausedPos())), true);
      } else {
        this.splice(now, T, rate);
        this.plan(now);
        this.deps.changed();
      }
    } else {
      this.deps.changed();
    }
    this.emit();
  }

  setLoop(loop: Loop): void {
    const next = normalizeLoop(loop);
    const cur = this.st.loop;
    if (next === cur || (next !== null && cur !== null && next.start === cur.start && next.end === cur.end)) return;
    this.st = { ...this.st, loop: next };
    this.replan();
    this.emit();
  }

  // ------------------------------------------------------------------ engine-facing

  /** Offset change (seconds of music per chart second origin); see the module header. */
  setShift(shift: number): void {
    if (!Number.isFinite(shift) || shift === this.shiftSec) return;
    this.shiftSec = shift;
    if (this.st.playing) {
      this.replan();
    } else {
      this.pausedChart = this.clampPos(this.pausedChart + shift) - shift;
      this.deps.changed();
    }
    this.emit();
  }

  /** Song length in music seconds (null = no music: unbounded). Stops playback. */
  setDuration(durationSec: number | null): void {
    if (this.st.playing) this.stop({ returnToStart: false });
    this.durationSec = durationSec !== null && Number.isFinite(durationSec) && durationSec > 0 ? durationSec : null;
    this.pausedChart = this.clampPos(this.pausedChart + this.shiftSec) - this.shiftSec;
    this.emit();
  }

  /** The output for the current rate changed (pitch-preserving toggle, music replaced): switch path while playing. */
  refreshOutput(): void {
    const now = this.deps.ctxNow();
    if (!this.st.playing || now === null) return;
    const next = this.deps.outputFor(this.st.rate);
    if (next === this.out) return;
    const T = now + START_LEAD_SEC;
    this.restartRun(now, T, this.clampPos(timelinePosAt(this.segs, T, this.pausedPos())), true);
    this.emit();
  }

  /** Element path: the element is buffering; hold the timeline until its clock moves again. */
  stall(): void {
    const now = this.deps.ctxNow();
    const out = this.out;
    if (!this.st.playing || now === null || out === null || out.kind !== 'element') return;
    const pos = timelinePosAt(this.segs, now, this.pausedPos());
    const lap = segmentAt(this.segs, now)?.lap ?? 0;
    for (const s of this.segs) out.cancel(s);
    this.segs = [this.segment(Infinity, pos, this.st.rate, lap)];
    this.endAt = null;
    this.resetClockTracking();
    this.deps.changed();
  }

  /** Periodic work (scheduler tick and animation frames): element clock, loop/end planning, song end. */
  service(now: number): void {
    if (!this.st.playing) return;
    const out = this.out;
    if (out !== null) {
      const sample = out.observe(now);
      if (sample !== null) this.applySample(sample, now);
    }
    this.plan(now);
    const visual = this.deps.audibleNow() ?? now;
    if (this.endAt !== null && visual >= this.endAt) {
      this.finish();
      return;
    }
    // Keep finished segments a while: the scheduler still maps recently due (and delayed) emissions through them.
    const horizon = Math.min(visual, now) - 1;
    while (this.segs.length > 1 && (this.segs[0]?.ctxEnd ?? Infinity) < horizon) this.segs.shift();
  }

  /** Notifies listeners without a state change (the output path changed: `MusicInfo.streaming`). */
  notify(): void {
    this.emit();
  }

  /** Stops everything (engine dispose). */
  dispose(): void {
    this.token++;
    if (this.st.playing) this.stop({ returnToStart: false });
    this.listeners.clear();
  }

  // ------------------------------------------------------------------ internals

  private isPlaying(): boolean {
    return this.st.playing;
  }

  private pausedPos(): number {
    return this.pausedChart + this.shiftSec;
  }

  private pausedOrPlayingChart(): number {
    return this.st.playing ? this.posNow('visual') - this.shiftSec : this.pausedChart;
  }

  private posNow(kind: 'visual' | 'audio'): number {
    if (!this.st.playing || this.segs.length === 0) return this.pausedPos();
    const t = kind === 'visual' ? this.deps.visualNow() : this.deps.ctxNow();
    if (t === null) return this.pausedPos();
    return timelinePosAt(this.segs, t, this.pausedPos());
  }

  /** Music position clamped to [min(0, shift), duration]. */
  private clampPos(pos: number): number {
    let p = Math.max(Math.min(0, this.shiftSec), pos);
    if (this.durationSec !== null) p = Math.min(p, this.durationSec);
    return p;
  }

  private segment(ctxStart: number, posStart: number, rate: number, lap: number): Segment {
    return { id: this.nextId++, run: this.run, lap, ctxStart, posStart, rate, ctxEnd: Infinity };
  }

  /** A run start: the element path waits for its first clock sample (provisional) unless it starts before music 0. */
  private startSegment(out: MusicOutput, ctxStart: number, pos: number, rate: number, lap: number): Segment {
    return this.segment(out.kind === 'element' && pos >= 0 ? Infinity : ctxStart, pos, rate, lap);
  }

  private startRun(mode: PlayMode): void {
    const now = this.deps.ctxNow();
    if (now === null) return;
    let pos = this.clampPos(this.pausedPos());
    if (this.durationSec !== null && pos >= this.durationSec - 1e-6) {
      // At the song end: start a loop that reaches the end again, otherwise start over from chart 0.
      const loop = this.loopPos();
      pos = loop !== null && loop.end >= this.durationSec - 1e-6 ? loop.start : this.clampPos(this.shiftSec);
    }
    this.pausedChart = pos - this.shiftSec;
    this.startChart = this.pausedChart;
    const out = this.deps.outputFor(this.st.rate);
    this.out = out;
    this.run++;
    const seg = this.startSegment(out, now + START_LEAD_SEC, pos, this.st.rate, 0);
    this.segs = [seg];
    this.endAt = null;
    this.resetClockTracking();
    this.endedNaturally = false;
    out.begin(seg, 'start');
    this.st = { ...this.st, playing: true, mode, startChartTime: Math.max(0, this.startChart) };
    this.plan(now);
    this.deps.playingChanged(true);
    this.deps.changed();
    this.emit();
  }

  /**
   * Restarts the music output from `pos` at context time `at`: a seek (a new play run) or, with `continuous`, a
   * path switch that continues the position (same run and lap, so nothing that already sounded plays again).
   */
  private restartRun(now: number, at: number, pos: number, continuous: boolean): void {
    const lap = continuous ? (segmentAt(this.segs, at)?.lap ?? 0) : 0;
    if (!continuous) this.run++;
    this.out?.halt();
    const out = this.deps.outputFor(this.st.rate);
    this.out = out;
    const seg = this.startSegment(out, at, pos, this.st.rate, lap);
    this.segs = [seg];
    this.endAt = null;
    this.resetClockTracking();
    out.begin(seg, 'start');
    this.plan(now);
    this.deps.changed();
  }

  private haltRun(): void {
    this.out?.halt();
    this.out = null;
    this.segs = [];
    this.endAt = null;
    this.resetClockTracking();
  }

  /** Continue at the same position with `rate` from context time T (cancels planned wraps/ends after T). */
  private splice(_now: number, T: number, rate: number): void {
    const out = this.out;
    const at = segmentAt(this.segs, T);
    if (out === null || at === undefined) return;
    if (isProvisional(at)) {
      // Not anchored yet (element path): keep holding, at the new rate.
      for (const s of this.segs) out.cancel(s);
      const seg = this.segment(Infinity, at.posStart, rate, at.lap);
      this.segs = [seg];
      out.begin(seg, 'splice');
      this.endAt = null;
      return;
    }
    const pos = segmentPosAt(at, T);
    const keep: Segment[] = [];
    for (const s of this.segs) {
      if (s.ctxStart >= T) {
        out.cancel(s);
        continue;
      }
      if (s.ctxEnd > T) {
        s.ctxEnd = T;
        out.end(s, T, 'splice');
      }
      keep.push(s);
    }
    const seg = this.segment(T, pos, rate, at.lap);
    keep.push(seg);
    this.segs = keep;
    this.endAt = null;
    out.begin(seg, 'splice');
  }

  /** Loop or offset changed: drop planned boundaries (seamless splice) and plan again. */
  private replan(): void {
    const now = this.deps.ctxNow();
    if (!this.st.playing || now === null) {
      this.deps.changed();
      return;
    }
    const planned = this.endAt !== null || this.segs.some((s) => Number.isFinite(s.ctxEnd));
    if (planned) this.splice(now, now + START_LEAD_SEC, this.st.rate);
    this.plan(now);
    this.deps.changed();
  }

  /**
   * Loop region in music seconds. A loop reaching past the song end wraps at the song end; a loop that starts at
   * or after the song end (or keeps less than MIN_LOOP_SEC of music) does not apply.
   */
  private loopPos(): { readonly start: number; readonly end: number } | null {
    const loop = this.st.loop;
    if (loop === null) return null;
    const start = loop.start + this.shiftSec;
    let end = loop.end + this.shiftSec;
    if (this.durationSec !== null) end = Math.min(end, this.durationSec);
    return end - start < MIN_LOOP_SEC ? null : { start, end };
  }

  /** Schedules loop wraps and the song end that fall within the planning horizon. */
  private plan(now: number): void {
    const out = this.out;
    if (!this.st.playing || out === null) return;
    for (let guard = 0; guard < 64; guard++) {
      const last = this.segs[this.segs.length - 1];
      if (last === undefined || isProvisional(last) || Number.isFinite(last.ctxEnd)) return;
      const loop = this.loopPos();
      let boundary: { readonly pos: number; readonly wrap: boolean } | null = null;
      // A loop reaching the song end also wraps a run that starts exactly at the end (a seek there while playing).
      const reachesEnd = loop !== null && this.durationSec !== null && loop.end >= this.durationSec - 1e-9;
      if (loop !== null && (last.posStart < loop.end - 1e-9 || reachesEnd)) boundary = { pos: loop.end, wrap: true };
      if (this.durationSec !== null && (boundary === null || this.durationSec < boundary.pos)) {
        boundary = { pos: this.durationSec, wrap: false };
      }
      if (boundary === null) return;
      const ctxB = Math.max(last.ctxStart, segmentCtxAt(last, boundary.pos));
      if (ctxB > now + PLAN_AHEAD_SEC) return;
      last.ctxEnd = ctxB;
      if (boundary.wrap && loop !== null) {
        out.end(last, ctxB, 'wrap');
        const seg = this.segment(ctxB, loop.start, last.rate, last.lap + 1);
        this.segs.push(seg);
        out.begin(seg, 'wrap');
      } else {
        out.end(last, ctxB, 'end');
        this.endAt = ctxB;
        return;
      }
    }
  }

  /**
   * Element clock sample. A provisional segment is anchored on it. Otherwise the error against the timeline is
   * smoothed, and a drift beyond REANCHOR_SEC re-anchors on the sample. After every anchor and every element restart
   * (seek, wrap, rate change) the next SETTLE_SAMPLES errors are averaged, and an average beyond SETTLE_TOLERANCE_SEC
   * shifts the anchor by it: a constant offset (start-up or seek delay of the element, a noisy anchor sample) does
   * not survive the first fraction of a second.
   */
  private applySample(sample: ClockSample, now: number): void {
    const cur = segmentAt(this.segs, sample.ctx);
    if (this.out === null || cur === undefined) return;
    if (isProvisional(cur)) {
      this.anchor(sample.ctx, sample.pos, cur, now);
      return;
    }
    if (cur.ctxStart > sample.ctx) return;
    const err = sample.pos - segmentPosAt(cur, sample.ctx);
    if (sample.restarted) {
      this.settleLeft = SETTLE_SAMPLES;
      this.settleSum = 0;
      this.drift = 0;
    }
    this.drift = this.drift * 0.6 + err * 0.4;
    if (Math.abs(this.drift) > REANCHOR_SEC) {
      this.anchor(sample.ctx, sample.pos, cur, now);
      return;
    }
    if (this.settleLeft === 0) return;
    this.settleSum += err;
    this.settleLeft--;
    if (this.settleLeft > 0) return;
    const mean = this.settleSum / SETTLE_SAMPLES;
    this.settleSum = 0;
    if (Math.abs(mean) <= SETTLE_TOLERANCE_SEC) return;
    this.reanchor(sample.ctx, segmentPosAt(cur, sample.ctx) + mean, cur, now);
  }

  /** Anchors the timeline on a sample and starts a settle window. */
  private anchor(ctx: number, pos: number, cur: Segment, now: number): void {
    this.reanchor(ctx, pos, cur, now);
    this.settleLeft = SETTLE_SAMPLES;
  }

  /**
   * Replaces the timeline with one segment playing `pos` at context time `ctx` (same run, lap and rate as `cur`).
   * A rate change still in its lead (spliced after `ctx`) is spliced in again at its time; wraps and the song end
   * are planned again.
   */
  private reanchor(ctx: number, pos: number, cur: Segment, now: number): void {
    const out = this.out;
    if (out === null) return;
    const rate = this.st.rate;
    const spliceAt =
      cur.rate === rate
        ? null
        : (this.segs.find((s) => s.ctxStart > ctx && s.rate === rate)?.ctxStart ?? now + START_LEAD_SEC);
    for (const s of this.segs) out.cancel(s);
    this.segs = [this.segment(ctx, pos, cur.rate, cur.lap)];
    this.endAt = null;
    this.resetClockTracking();
    if (spliceAt !== null) this.splice(now, Math.max(spliceAt, ctx), rate);
    this.plan(now);
    this.deps.changed();
  }

  private resetClockTracking(): void {
    this.drift = 0;
    this.settleLeft = 0;
    this.settleSum = 0;
  }

  /** The song ended (visual clock reached the end). */
  private finish(): void {
    this.haltRun();
    this.pausedChart = (this.durationSec ?? this.pausedPos()) - this.shiftSec;
    this.endedNaturally = true;
    this.st = { ...this.st, playing: false };
    this.deps.playingChanged(false);
    this.deps.changed();
    this.emit();
  }

  private update(patch: Partial<TransportState>): void {
    this.st = { ...this.st, ...patch };
    this.emit();
  }

  private emit(): void {
    const s = this.st;
    for (const cb of [...this.listeners]) {
      try {
        cb(s);
      } catch (e) {
        queueMicrotask(() => {
          throw e;
        });
      }
    }
  }
}

function normalizeLoop(loop: Loop): Loop {
  if (loop === null) return null;
  if (!Number.isFinite(loop.start) || !Number.isFinite(loop.end)) return null;
  const start = Math.max(0, loop.start);
  if (loop.end - start < MIN_LOOP_SEC) return null;
  return { start, end: loop.end };
}
